import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions, ListProviderPhoneRequest,
    ProviderPhone
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './phonelist.styles';
import {PhoneDetail} from "../claims/components/claim-timeline-table/phone-detail";

interface IPhoneProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IPhoneState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderPhone>[] = [
    {
        id: 'Id',
        Header: 'Id',
        accessor: 'id',
        Cell: DataTableCell('String'),
        show: true,
    },
    {
        id: 'Agent',
        Header: 'Agent',
        accessor: 'agent',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'START_DATE',
        Header: 'Start date',
        accessor: 'start',
        Cell: DataTableCell('DateTime'),
        defaultSort: { id: 'START_DATE', desc: true },
        show: true,
    },
    {
        id: 'END_DATE',
        Header: 'End Date',
        accessor: 'end',
        Cell: DataTableCell('DateTime'),
        show: true,
    },
];
const filterSchemas: IFilterSchema<keyof ListProviderPhoneRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    }
];

class PhoneListPage extends React.Component<IPhoneProps, IPhoneState> {
    private presenter: DataTablePresenter<ProviderPhone>;

    constructor(props: IPhoneProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderPhone>({
            name: 'phone-list',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Phone calls';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('phone-list');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('phone-list');
                this.authContext.setGroup('phone-list');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const phoneListing = await this.props.rootStore.claimStore.loadPhoneListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(phoneListing.items || []);
                this.presenter.onDataTotalChange(phoneListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderPhone>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderPhone>): React.ReactElement => {
        return (
            <S.Expand>
                <PhoneDetail phone={row.original} />
            </S.Expand>
        );
    };
}

export const PhoneList: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <PhoneListPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
